import React, { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { getDatabase, onValue, ref, remove, update } from "firebase/database";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FiEye, FiTrash2 } from "react-icons/fi";
import { FaFacebook } from "react-icons/fa";
import zaloIcon from "./images/zalo.png";

const DAY_LABELS = {
  monday: "Thứ 2",
  tuesday: "Thứ 3",
  wednesday: "Thứ 4",
  thursday: "Thứ 5",
  friday: "Thứ 6",
  saturday: "Thứ 7",
  sunday: "Chủ nhật",
};

const DAY_TO_INDEX = {
  sunday: 0,
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
};

const formatDateTime = (timestamp) => {
  if (!timestamp) return "—";
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("vi-VN", {
    hour12: false,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const computeQueueCounts = (postQueue) => {
  const items = Array.isArray(postQueue)
    ? postQueue
    : Object.values(postQueue || {});

  const counts = {
    total: items.length,
    pending: 0,
    in_progress: 0,
    posted: 0,
    failed: 0,
  };

  items.forEach((item) => {
    const status = item.status || "pending";
    if (counts[status] !== undefined) {
      counts[status] += 1;
    }
  });

  return counts;
};

const computeNextRun = (selectedDays = {}, times = []) => {
  if (!times || times.length === 0) return null;
  const enabledDays = Object.entries(selectedDays)
    .filter(([, value]) => Boolean(value))
    .map(([day]) => DAY_TO_INDEX[day])
    .filter((value) => value !== undefined);

  if (enabledDays.length === 0) return null;

  const sortedTimes = [...times].filter(Boolean).sort();
  if (sortedTimes.length === 0) return null;

  const now = new Date();

  for (let offset = 0; offset < 14; offset += 1) {
    const candidateDate = new Date(now);
    candidateDate.setDate(now.getDate() + offset);
    const candidateDayIndex = candidateDate.getDay();

    if (!enabledDays.includes(candidateDayIndex)) continue;

    for (const time of sortedTimes) {
      const [hour, minute] = time.split(":").map(Number);
      if (Number.isNaN(hour) || Number.isNaN(minute)) continue;

      const runDate = new Date(candidateDate);
      runDate.setHours(hour, minute, 0, 0);

      if (runDate > now) {
        return runDate;
      }
    }
  }

  return null;
};

const computeNextRunForFixedDates = (fixedDateSchedules = []) => {
  if (!Array.isArray(fixedDateSchedules) || fixedDateSchedules.length === 0) {
    return null;
  }

  const now = new Date();
  let nextRun = null;

  fixedDateSchedules.forEach((schedule) => {
    // Skip completed schedules
    if (schedule.completed) return;

    const scheduleDate = schedule.date;
    const scheduleTimes = schedule.times || [];

    if (!scheduleDate || scheduleTimes.length === 0) return;

    scheduleTimes.forEach((time) => {
      const [hour, minute] = time.split(":").map(Number);
      if (Number.isNaN(hour) || Number.isNaN(minute)) return;

      const runDate = new Date(scheduleDate);
      runDate.setHours(hour, minute, 0, 0);

      if (runDate > now) {
        if (!nextRun || runDate < nextRun) {
          nextRun = runDate;
        }
      }
    });
  });

  return nextRun;
};

const getScheduleDisplayInfo = (config) => {
  const scheduleMode = config.scheduleMode || "weekly";

  if (scheduleMode === "fixed-dates") {
    const fixedDateSchedules = config.fixedDateSchedules || [];
    const totalSchedules = fixedDateSchedules.length;
    const completedSchedules = fixedDateSchedules.filter(
      (s) => s.completed
    ).length;
    const pendingSchedules = totalSchedules - completedSchedules;

    return {
      mode: "fixed-dates",
      displayText: `${pendingSchedules}/${totalSchedules} lịch chờ đăng`,
      schedules: fixedDateSchedules,
      nextRun: computeNextRunForFixedDates(fixedDateSchedules),
    };
  }

  // Weekly mode
  const dayOrder = [
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
    "sunday",
  ];

  const daysSelected = dayOrder
    .filter((day) => config.selectedDays?.[day])
    .map((day) => DAY_LABELS[day])
    .filter(Boolean);

  const scheduleTimes = Array.isArray(config.times) ? config.times : [];

  return {
    mode: "weekly",
    displayText:
      daysSelected.length > 0 ? daysSelected.join(", ") : "Chưa chọn ngày",
    times: scheduleTimes,
    nextRun: computeNextRun(config.selectedDays, config.times),
  };
};

const ManualPostConfigScreen = () => {
  const navigate = useNavigate();
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [selectedFanpage, setSelectedFanpage] = useState("all");
  const [processingConfigId, setProcessingConfigId] = useState(null);
  const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);
  const [isFanpageDropdownOpen, setIsFanpageDropdownOpen] = useState(false);
  const statusDropdownRef = useRef(null);
  const fanpageDropdownRef = useRef(null);

  useEffect(() => {
    const userId = localStorage.getItem("userId");
    if (!userId) {
      navigate("/");
      return;
    }

    const db = getDatabase();
    const configsRef = ref(db, `manualPosts/${userId}`);

    const unsubscribe = onValue(
      configsRef,
      (snapshot) => {
        const list = [];
        snapshot.forEach((child) => {
          list.push({ id: child.key, ...child.val() });
        });
        setConfigs(list);
        setLoading(false);
      },
      (error) => {
        console.error("Failed to load manual configs", error);
        toast.error("Không thể tải danh sách thiết lập thủ công.");
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [navigate]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        statusDropdownRef.current &&
        !statusDropdownRef.current.contains(event.target)
      ) {
        setIsStatusDropdownOpen(false);
      }
      if (
        fanpageDropdownRef.current &&
        !fanpageDropdownRef.current.contains(event.target)
      ) {
        setIsFanpageDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const fanpageFilterOptions = useMemo(() => {
    const accountNames = new Set();
    configs.forEach((config) => {
      const accounts = config.selectedAccounts || config.selectedFanpages || [];
      accounts.forEach((account) => {
        if (account?.name) accountNames.add(account.name);
      });
    });
    return Array.from(accountNames);
  }, [configs]);

  const filteredConfigs = useMemo(() => {
    return configs.filter((config) => {
      const accounts = config.selectedAccounts || config.selectedFanpages || [];

      const matchesSearch =
        config.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        accounts.some((account) =>
          account?.name?.toLowerCase().includes(searchTerm.toLowerCase())
        );

      const matchesStatus =
        filterStatus === "all"
          ? true
          : filterStatus === "active"
          ? config.autoPostEnabled !== false
          : config.autoPostEnabled === false;

      const matchesFanpage =
        selectedFanpage === "all"
          ? true
          : accounts.some(
              (account) => account?.name === selectedFanpage
            );

      return matchesSearch && matchesStatus && matchesFanpage;
    });
  }, [configs, filterStatus, searchTerm, selectedFanpage]);

  const handleToggleStatus = async (config) => {
    const userId = localStorage.getItem("userId");
    if (!userId) return;

    setProcessingConfigId(config.id);
    const db = getDatabase();
    const configRef = ref(db, `manualPosts/${userId}/${config.id}`);

    try {
      await update(configRef, {
        autoPostEnabled: !(config.autoPostEnabled !== false),
        updatedAt: Date.now(),
      });
      toast.success("Đã cập nhật trạng thái thiết lập.");
    } catch (error) {
      console.error("Failed to update manual config status", error);
      toast.error("Không thể cập nhật trạng thái. Vui lòng thử lại.");
    } finally {
      setProcessingConfigId(null);
    }
  };

  const handleDelete = async (configId) => {
    const confirmed = window.confirm(
      "Bạn có chắc muốn xóa thiết lập đăng bài thủ công này?"
    );
    if (!confirmed) return;

    const userId = localStorage.getItem("userId");
    if (!userId) return;

    const db = getDatabase();
    const configRef = ref(db, `manualPosts/${userId}/${configId}`);

    try {
      await remove(configRef);
      toast.success("Đã xóa thiết lập thủ công.");
    } catch (error) {
      console.error("Failed to delete manual config", error);
      toast.error("Không thể xóa thiết lập. Vui lòng thử lại.");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 py-4 md:px-8 md:py-8">
        <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 mb-4 sm:mb-6 md:mb-8 text-center">
          DANH SÁCH LỊCH ĐĂNG CHO BÀI VIẾT CÓ SẴN
        </h1>

        <div className="mb-6 space-y-4 sm:space-y-0 sm:grid sm:grid-cols-2 md:flex md:flex-row md:gap-4">
          <div className="sm:col-span-2 md:flex-1">
            <input
              type="text"
              placeholder="Tìm kiếm theo tên thiết lập hoặc tên tài khoản..."
              className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="md:w-40 relative" ref={statusDropdownRef}>
            <button
              onClick={() => setIsStatusDropdownOpen(!isStatusDropdownOpen)}
              className={`w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 flex justify-between items-center ${
                filterStatus !== "all" ? "bg-white" : "bg-gray-50"
              }`}
            >
              <span>
                {filterStatus === "all"
                  ? "Trạng thái"
                  : filterStatus === "active"
                  ? "Đang chạy"
                  : "Đã tắt"}
              </span>
              <svg
                className={`w-4 h-4 transition-transform ${
                  isStatusDropdownOpen ? "rotate-180" : "rotate-0"
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M19 9l-7 7-7-7"
                ></path>
              </svg>
            </button>
            {isStatusDropdownOpen && (
              <div className="absolute z-10 w-full bg-white border border-gray-300 rounded-lg mt-2">
                <button
                  onClick={() => {
                    setFilterStatus("all");
                    setIsStatusDropdownOpen(false);
                  }}
                  className="w-full px-4 py-2 text-left hover:bg-gray-100"
                >
                  Trạng thái
                </button>
                <button
                  onClick={() => {
                    setFilterStatus("active");
                    setIsStatusDropdownOpen(false);
                  }}
                  className="w-full px-4 py-2 text-left hover:bg-gray-100"
                >
                  Đang chạy
                </button>
                <button
                  onClick={() => {
                    setFilterStatus("inactive");
                    setIsStatusDropdownOpen(false);
                  }}
                  className="w-full px-4 py-2 text-left hover:bg-gray-100"
                >
                  Đã tắt
                </button>
              </div>
            )}
          </div>

          <div className="md:w-40 relative" ref={fanpageDropdownRef}>
            <button
              onClick={() => setIsFanpageDropdownOpen(!isFanpageDropdownOpen)}
              className={`w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 flex justify-between items-center ${
                selectedFanpage !== "all" ? "bg-white" : "bg-gray-50"
              }`}
            >
              <span className="truncate">
                {selectedFanpage === "all" ? "Tài khoản" : selectedFanpage}
              </span>
              <svg
                className={`w-4 h-4 transition-transform ${
                  isFanpageDropdownOpen ? "rotate-180" : "rotate-0"
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M19 9l-7 7-7-7"
                ></path>
              </svg>
            </button>
            {isFanpageDropdownOpen && (
              <div className="absolute z-10 w-full bg-white border border-gray-300 rounded-lg mt-2">
                <button
                  onClick={() => {
                    setSelectedFanpage("all");
                    setIsFanpageDropdownOpen(false);
                  }}
                  className="w-full px-4 py-2 text-left hover:bg-gray-100"
                >
                  Tài khoản
                </button>
                {fanpageFilterOptions.map((option) => (
                  <button
                    key={option}
                    onClick={() => {
                      setSelectedFanpage(option);
                      setIsFanpageDropdownOpen(false);
                    }}
                    className="w-full px-4 py-2 text-left hover:bg-gray-100 truncate"
                    title={option}
                  >
                    {option}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {filteredConfigs.length === 0 ? (
          <div className="text-center text-gray-500 py-8">
            {configs.length === 0
              ? "Không có cấu hình nào được tạo. Bạn có thể tạo cấu hình mới"
              : "Không tìm thấy kết quả phù hợp với điều kiện tìm kiếm"}
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {filteredConfigs.map((config) => {
              const queueCounts = computeQueueCounts(config.postQueue);
              const scheduleInfo = getScheduleDisplayInfo(config);

              return (
                <div
                  key={config.id}
                  onClick={() =>
                    navigate("/manual-post", { state: { config } })
                  }
                  className="bg-white rounded-xl shadow-lg overflow-hidden transform transition duration-300 hover:scale-105 cursor-pointer"
                >
                  <div className="p-6">
                    <div className="flex justify-between items-start mb-4">
                      <h3 className="font-bold text-xl text-gray-800 line-clamp-2">
                        {config.name || "Thiết lập thủ công"}
                      </h3>
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-gray-600">
                        <div className="flex items-center">
                          <svg
                            className="w-5 h-5 mr-2"
                            fill="none"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                          </svg>
                          <span className="text-sm">
                            {scheduleInfo.mode === "weekly"
                              ? scheduleInfo.times.length > 0
                                ? scheduleInfo.times.join(" | ")
                                : "Chưa đặt giờ"
                              : "Ngày cố định"}
                          </span>
                        </div>
                        <div
                          className={`px-3 py-1 rounded-full text-sm font-medium ${
                            config.autoPostEnabled !== false
                              ? "bg-blue-100 text-blue-800"
                              : "bg-gray-100 text-gray-600"
                          }`}
                        >
                          {config.autoPostEnabled !== false
                            ? "🔵 Đang chạy"
                            : "⚪ Đã tắt"}
                        </div>
                      </div>

                      <div className="flex items-center text-gray-600">
                        <svg
                          className="w-5 h-5 mr-2"
                          fill="none"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                        </svg>
                        <span className="text-sm">
                          {scheduleInfo.displayText}
                        </span>
                      </div>

                      <div className="flex items-center text-gray-600">
                        <svg
                          className="w-5 h-5 mr-2 flex-shrink-0"
                          fill="none"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"></path>
                        </svg>
                        <div className="flex flex-wrap items-center gap-1">
                          {(() => {
                            const accounts = config.selectedAccounts || config.selectedFanpages || [];
                            if (accounts.length === 0) return <span>Chưa chọn tài khoản</span>;
                            return accounts.map((account, idx) => {
                              const name = account.name || account.page_name || account.id;
                              const isZalo = (account.platform || "facebook") === "zalo";
                              return (
                                <span key={idx} className="inline-flex items-center mr-1 text-sm text-gray-700">
                                  {isZalo ? (
                                    <img src={zaloIcon} alt="Zalo" className="w-4 h-4 mr-1 rounded-sm object-cover" />
                                  ) : (
                                    <FaFacebook className="text-blue-600 mr-1" />
                                  )}
                                  {name}{idx < accounts.length - 1 ? "," : ""}
                                </span>
                              );
                            });
                          })()}
                        </div>
                      </div>

                      <div className="flex items-center text-gray-600">
                        <svg
                          className="w-5 h-5 mr-2"
                          fill="none"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path d="M9 5H7a2 2 0 00-2 2v11a2 2 0 002 2h2m0-13h6a2 2 0 012 2v11a2 2 0 01-2 2h-6m0-13v13"></path>
                        </svg>
                        <span>
                          {queueCounts.pending} chờ • {queueCounts.posted} đã
                          đăng • {queueCounts.failed} lỗi
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <ToastContainer position="top-right" autoClose={3000} />
      </div>
    </div>
  );
};

export default ManualPostConfigScreen;
